import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type Clip, ClipType } from '@air/api/types';
import { buildClientImgixUrl } from '@air/component-imgix-image';

import { AssetGalleryCardShortInfo } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardShortInfo';
import { RiveThumbnail } from '~/components/RiveThumbnail';
import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { DEFAULT_THUMBNAIL_DIMENSIONS } from '~/components/TableView/NameCell/dimensions';
import { VideoScrubbableThumbnail } from '~/components/VideoScrubbableThumbnail';
import { useThumbnailPreference } from '~/hooks/useThumbnailPreference';
import { useVideoScrubbableThumbnail } from '~/hooks/useVideoScrubbableThumbnail';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import { ClipIconSize } from '~/types/clip-icon-size';
import { isRiveFile } from '~/utils/RiveUtils';

export type AssetCellThumbnailProps = {
  clip: Clip;
  description: string;
};

export const AssetCellThumbnail = memo(({ clip, description }: AssetCellThumbnailProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const { getPreference } = useThumbnailPreference();
  const boardId = useSelector(CentralizedBoardSelectors.boardId);
  const thumbnailPreference = useMemo(() => getPreference(boardId), [boardId, getPreference]);

  const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } =
    useVideoScrubbableThumbnail({
      spritesheet: clip.assets.spriteSheet,
      containerDimensions: DEFAULT_THUMBNAIL_DIMENSIONS,
    });

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onMouseLeave();
  }, [onMouseLeave]);

  const spriteSheetUrl = useMemo(
    () =>
      clip.type === ClipType.video && clip.assets.spriteSheet?.src
        ? buildClientImgixUrl(clip.assets.spriteSheet.src, {
            w: DEFAULT_THUMBNAIL_DIMENSIONS.width * 10,
            dpr: window.devicePixelRatio,
          })
        : undefined,
    [clip.type, clip.assets.spriteSheet?.src],
  );

  const videoScrubbableThumbnail = useMemo(
    () =>
      spriteSheetUrl && isHovered ? (
        <VideoScrubbableThumbnail
          backgroundImageRef={backgroundImageRef}
          indicatorRef={indicatorRef}
          src={spriteSheetUrl}
          style={backgroundImageStyles}
        />
      ) : null,
    [spriteSheetUrl, isHovered, backgroundImageRef, indicatorRef, backgroundImageStyles],
  );

  const videoDuration = useMemo(
    () =>
      isHovered ? null : (
        <div className="absolute right-1 bottom-1 z-10">
          <AssetGalleryCardShortInfo clip={clip} />
        </div>
      ),
    [clip, isHovered],
  );

  // If this is a Rive file, render the Rive animation
  // Only render if the original asset URL is available and not empty
  if (isRiveFile(clip) && clip.assets.original && clip.assets.original.trim() !== '') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} role="img">
        <RiveThumbnail
          src={clip.assets.original}
          width={DEFAULT_THUMBNAIL_DIMENSIONS.width}
          height={DEFAULT_THUMBNAIL_DIMENSIONS.height}
          borderRadius={4}
        />
      </div>
    );
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onMouseMove={onMouseMove} role="img">
      <ClipThumbnail
        ext={clip.ext}
        status={clip.status}
        type={clip.type}
        borderRadius={4}
        description={description}
        src={clip.assets.image}
        iconSize={ClipIconSize.small}
        hideText={true}
        style={{
          padding: 0,
        }}
        thumbnailPreference={thumbnailPreference}
        {...DEFAULT_THUMBNAIL_DIMENSIONS}
      />
      {videoScrubbableThumbnail}
      {videoDuration}
    </div>
  );
});

AssetCellThumbnail.displayName = 'AssetCellThumbnail';
